import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faDiscord } from '@fortawesome/free-brands-svg-icons';
import { ImportSessionStorage } from 'utils/importSessionStorage';
import { beginDiscordLogin } from 'utils/play/discordAuth';
import { PLAY_ERROR_MESSAGES } from 'utils/play/playErrors';
import '../screens/Invite/InvitePage.css';

interface Props {
  /** Relative /play path to come back to after either entry */
  returnTo: string;
  title?: string;
  footnote?: string;
}

/**
 * The Play auth card (Decisions 3, 4, 24; Eric's ruling 31 of 2026-10-30):
 * Continue with Discord (primary) and the existing TradeMagic login. The
 * invite page and the signed-out /play/host render the same card. Both
 * entries return to `returnTo` after auth.
 */
export const PlayAuthCard: React.FC<Props> = ({ returnTo, title = 'Join in under a minute', footnote = 'No address or phone number needed to play.' }) => {
  const navigate = useNavigate();
  const [discordNote, setDiscordNote] = useState<string | null>(null);

  const startDiscord = async () => {
    setDiscordNote(null);
    const result = await beginDiscordLogin(returnTo);
    if (result === 'unavailable') setDiscordNote(PLAY_ERROR_MESSAGES.DISCORD_NOT_CONFIGURED);
    else if (result === 'error') setDiscordNote(PLAY_ERROR_MESSAGES.DISCORD_ERROR);
  };

  const signInWithTradeMagic = () => {
    ImportSessionStorage.setReturnTo(returnTo);
    navigate('/welcome?tab=signin');
  };

  return (
    <section className="play-card play-invite__join" data-testid="play-auth-card">
      <h2 className="play-invite__join-title">{title}</h2>
      <button type="button" className="play-discord-button" onClick={() => void startDiscord()} data-testid="play-discord-login">
        <FontAwesomeIcon icon={faDiscord as IconProp} />
        Continue with Discord
      </button>
      {discordNote && (
        <p className="play-error" role="alert" data-testid="play-discord-note">
          {discordNote}
        </p>
      )}
      <p className="play-invite__or">or</p>
      <button type="button" className="play-secondary-button" onClick={signInWithTradeMagic} data-testid="play-tm-login">
        I have a TradeMagic account
      </button>
      <p className="play-invite__footnote">{footnote}</p>
    </section>
  );
};

export default PlayAuthCard;
